import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Immutable from 'immutable'
import { useDispatch, useSelector } from 'react-redux'

import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import { getBasketProducts } from 'selectors/basket'
import { getBasket } from 'selectors/root'

import {
  buildProductLimitMaps,
  getImmutableRecord,
  getUpsellProductLimitReached,
} from './basketUpsellLimits'
import {
  type BasketUpsellProductActionSource,
  buildBasketUpsellViewModel,
  buildProductDetailViewModel,
} from './basketUpsellViewModel'
import { useBasketUpsellFeatureFlag } from './useBasketUpsellFeatureFlag'
import { useFetchBasketUpsell } from './useFetchBasketUpsell'

type UseBasketUpsellInput = {
  basketDate?: string
  menuUuid?: string
  numPortions: number
  postcode?: string
  selectedRecipeIds: string[]
}

type DisplayRthCarouselEvent = {
  action: 'display_rth_carousel'
  properties: {
    menuUuid?: string
    productsShown: string[]
  }
}

export const useBasketUpsell = ({
  basketDate,
  menuUuid,
  numPortions,
  postcode,
  selectedRecipeIds,
}: UseBasketUpsellInput) => {
  const dispatch = useDispatch()
  const isBasketUpsellEnabled = useBasketUpsellFeatureFlag()
  const basket = useSelector(getBasket)
  const basketProducts =
    useSelector((state) => getBasketProducts(state as any)) ?? Immutable.Map<string, number>()
  const [productDetailsId, setProductDetailsId] = useState<string | null>(null)
  const hasTrackedCarouselDisplay = useRef(false)

  const shouldFetchBasketUpsells = isBasketUpsellEnabled && !!menuUuid && !!basketDate && !!postcode
  const { hasFailed, response: upsellResponse } = useFetchBasketUpsell({
    basketDate,
    enabled: shouldFetchBasketUpsells,
    menuUuid,
    numPortions,
    postcode,
    selectedRecipeIds,
  })

  const upsellCarousel = upsellResponse?.carousel
  const basketProductQuantities = useMemo(
    () => basketProducts.toJS() as Record<string, number>,
    [basketProducts],
  )
  const localBasket = useMemo(
    () => ({
      products: basketProductQuantities,
      gifts: getImmutableRecord(basket.get('gifts')),
    }),
    [basket, basketProductQuantities],
  )
  const productLimitMaps = useMemo(
    () => (upsellResponse ? buildProductLimitMaps(upsellResponse) : null),
    [upsellResponse],
  )
  const getLimitReached = useCallback(
    (productId: string) => {
      if (!upsellResponse) return false
      if (!productLimitMaps) return false

      return getUpsellProductLimitReached(productId, localBasket, upsellResponse, productLimitMaps)
    },
    [localBasket, productLimitMaps, upsellResponse],
  )
  const addUpsellProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      const product = upsellResponse?.products[productId]
      if (!product) return

      const qty = basketProductQuantities[product.id] ?? 0
      const outOfStock = product.stock - qty <= 0
      const limitReached = getLimitReached(product.id)

      if (outOfStock || limitReached) return

      // Upsell products are scoped to this response, so dispatch the existing basket action
      // directly instead of going through the product-slice thunk.
      dispatch({
        type: actionTypes.BASKET_PRODUCT_ADD,
        productId: product.id,
        unsaved: true,
        trackingData: {
          actionType: actionTypes.BASKET_PRODUCT_ADD,
          menuUuid,
          productId: product.id,
          source,
        },
      })
    },
    [basketProductQuantities, dispatch, getLimitReached, menuUuid, upsellResponse?.products],
  )
  const removeUpsellProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      const product = upsellResponse?.products[productId]
      if (!product) return

      if ((basketProductQuantities[product.id] ?? 0) <= 0) return

      // Keep the basket reducer/tracking action shape, but source product properties from the
      // upsell response because these products are not in the global product Redux slice.
      dispatch({
        type: actionTypes.BASKET_PRODUCT_REMOVE,
        productId: product.id,
        unsaved: true,
        trackingData: {
          actionType: actionTypes.BASKET_PRODUCT_REMOVE,
          menuUuid,
          productId: product.id,
          source,
        },
      })
    },
    [basketProductQuantities, dispatch, menuUuid, upsellResponse?.products],
  )
  const shouldShowBasketUpsells = shouldFetchBasketUpsells && !hasFailed && !!upsellCarousel

  const basketUpsell = buildBasketUpsellViewModel({
    basketProductQuantities,
    getLimitReached,
    onAddProduct: addUpsellProduct,
    onClickProduct: (product) => setProductDetailsId(product.id),
    onRemoveProduct: removeUpsellProduct,
    shouldShowBasketUpsells,
    upsellResponse,
  })

  useEffect(() => {
    if (hasTrackedCarouselDisplay.current) return
    if (!basketUpsell || basketUpsell.products.length < 2) return

    queueTrackingEvent<DisplayRthCarouselEvent>({
      action: 'display_rth_carousel',
      properties: {
        menuUuid,
        productsShown: basketUpsell.products.map((product) => product.id),
      },
    })

    hasTrackedCarouselDisplay.current = true
  }, [basketUpsell, menuUuid])

  const productDetail = buildProductDetailViewModel({
    basketProductQuantities,
    getLimitReached,
    onAddProduct: addUpsellProduct,
    onClose: () => setProductDetailsId(null),
    onRemoveProduct: removeUpsellProduct,
    productDetailsId,
    products: upsellResponse?.products ?? {},
  })

  return {
    basketUpsell,
    productDetail,
  }
}
